<template>
  <div v-if="genericPage && genericPageId">
    <DKGenericPageHeader
      ref="genericHeader"
      :genericPage="genericPage"
      :headerType="headerType"
      :locale="$locale"
      @clickPageNavbarItem="onClickPageNavbarItem"
    ></DKGenericPageHeader>
    <GenericPageRenderer :fragments="genericPageFragments">
    </GenericPageRenderer>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import GenericPageRenderer from '@/components/GenericPageRenderer.vue';
import { DKGenericPageHeader } from '@dormakaba/dormakaba-components';
import { useActiveMeta, useMeta } from 'vue-meta';
import { generateMetaInfo } from '@/helpers/metainfo';
import { DKGenericPageContentFragmentsItem } from '@/models/DKGenericPageContentFragmentsItem';
import { DKGenericPage } from '@/models/DKGenericPage';
import { DKMetaInfo } from '@/models/DKMetaInfo';
import { PageHeaderType } from '@/store/app/models/PageHeaderType';

import { navigateToAnchor } from '@/helpers/link';
import { formatHash } from '@/helpers/format-hash';

@Component({
  components: {
    GenericPageRenderer,
    DKGenericPageHeader,
  },
})
export default class GenericPage extends Vue {
  get metaInfo(): DKMetaInfo {
    return {
      meta: (this.$metaInfoRef as any).meta,
      page: this.genericPage,
      locale: this.$i18n?.locale || 'en-US',
      $route: this.$route,
    };
  }

  get genericPageId(): string | null {
    return this.$store.getters['navigation/pageIdBySlug'](
      this.$route.params.slug
    );
  }

  get genericPage(): DKGenericPage {
    return this.$store.getters['content/genericPage'];
  }

  get genericPageFragments(): DKGenericPageContentFragmentsItem[] {
    const genericPage = this.genericPage;
    const items: DKGenericPageContentFragmentsItem[] = [];

    if (genericPage.contentFragments) {
      items.push(...genericPage.contentFragments);
    }

    return items;
  }

  get headerType(): PageHeaderType {
    return this.$store.getters['headerType'];
  }

  onClickPageNavbarItem(event: MouseEvent) {
    this.$trackInPage(event, formatHash(event as unknown as string, false));
    navigateToAnchor(event as unknown as string);
  }

  created() {
    this.$metaInfoRef = useMeta({ ...useActiveMeta() });
  }

  async mounted() {
    await this.$store.dispatch('content/fetchGenericPage', {
      id: this.genericPageId,
      locale: this.$i18n.locale,
      $route: this.$route,
      $router: this.$router,
    });

    this.$route.meta.defaultFooter = this.genericPage.footer;

    const meta = generateMetaInfo(this.metaInfo);

    this.$trackView('generic_page', meta.title);

    const transparentHeaders = {
      MEDIUM_HERO_HEADER: this.$refs.genericHeader.$el,
      BIG_HERO_HEADER: this.$refs.genericHeader.$el.nextElementSibling,
    };

    const currentHeader = Object.keys(transparentHeaders).find(
      (key) => PageHeaderType[key] === this.headerType
    );

    currentHeader
      ? this.$store.commit(
          'navigation/setNavbarTransparentElement',
          transparentHeaders[currentHeader]
        )
      : this.$store.commit('navigation/setNavbarTransparentElement');
  }

  async serverPrefetch() {
    return Promise.all([
      this.$store.dispatch('content/fetchGenericPage', {
        id: this.genericPageId,
        locale: this.$i18n.locale,
        $route: this.$route,
        $router: this.$router,
      }),
    ]).then(() => generateMetaInfo(this.metaInfo));
  }
}
</script>
